import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { __, getLocalized, setTableParams } from '../../../core/utils';
import { setNotification } from '../../../redux/actions/general/notification';
import {
  listPlanningTemplates,
  updatePlanningTemplate,
} from '../../../redux/actions/table/planning_library';
import Button from '../../../components/Button';
import PlanningTemplateTable from './PlanningTemplateTable';
import PlanningTemplateLibraryDropdown from './PlanningTemplateLibraryDropdown';
import ArrowDownMiddleIcon from '../../../assets/images/arrow-down-middle-15x15.svg';
import {
  createEmptyPlanningTemplate,
  createPlanningTemplateNodeId,
  normalizePlanningTemplateNodes,
  planningTemplateCounts,
  planningTemplateFromResponse,
  planningTemplateNodesToPayload,
} from './helpers';
import styles from '../../../styles/views/company/PlanningTemplates.scss';

const PlanningTemplateBuilder = ({
  canAddTemplate = true,
  canDeleteSystem = false,
  canEditSystem = false,
  isSystem = false,
  languageId,
  table,
}) => {
  const [depth, setDepth] = useState(3);
  const [libraryPicker, setLibraryPicker] = useState();
  const draft = table.template_draft || createEmptyPlanningTemplate(languageId);
  const readOnly = draft.id
    ? Boolean(draft.is_system && !canEditSystem)
    : !canAddTemplate;

  useEffect(() => {
    listPlanningTemplates(isSystem);

    if (!table.template_draft) {
      setTableParams('planning_library', {
        template_draft: createEmptyPlanningTemplate(languageId),
        template_dirty: false,
      });
    }
  }, []);

  const selectLibraryItem = (item) => {
    if (!libraryPicker || readOnly) {
      return;
    }

    const siblings = _.filter(draft.nodes, {
      parent_client_id: libraryPicker.parentClientId || null,
    });
    const afterNode = _.find(draft.nodes, {
      client_id: libraryPicker.afterClientId,
    });
    const position = afterNode ? afterNode.position + 1 : siblings.length;
    const nodes = _.map(draft.nodes, (node) =>
      node.parent_client_id === (libraryPicker.parentClientId || null) &&
      node.position >= position
        ? { ...node, position: node.position + 1 }
        : node
    );

    nodes.push({
      client_id: createPlanningTemplateNodeId(),
      parent_client_id: libraryPicker.parentClientId || null,
      planning_library_item_id: item.id,
      type: item.type,
      code: item.code,
      name: getLocalized(item.name, languageId),
      description: getLocalized(item.description, languageId),
      is_system: item.is_system,
      depth:
        libraryPicker.type === 'stage'
          ? 0
          : libraryPicker.type === 'substage'
          ? 1
          : 2,
      position,
    });

    setTableParams('planning_library', {
      template_draft: {
        ...draft,
        nodes: normalizePlanningTemplateNodes(nodes),
      },
      template_dirty: true,
    });
    setLibraryPicker(undefined);
  };

  const updateNodes = (nodes) => {
    if (readOnly) {
      return;
    }

    setTableParams('planning_library', {
      template_draft: {
        ...draft,
        nodes: normalizePlanningTemplateNodes(nodes),
      },
      template_dirty: true,
    });
  };

  const openLibrary = (type, options = {}, event) => {
    if (readOnly) {
      return;
    }

    const target = event?.currentTarget || event?.target;
    const rect = target?.getBoundingClientRect?.();
    const left = Math.max(
      10,
      Math.min(event?.clientX || rect?.left || 20, window.innerWidth - 370)
    );
    const top = Math.max(
      10,
      Math.min(rect?.bottom || event?.clientY || 70, window.innerHeight - 430)
    );

    setLibraryPicker({
      type,
      parentClientId: options.parentClientId || null,
      afterClientId: options.afterClientId || null,
      left,
      top,
      key: `${Date.now()}-${type}`,
    });
  };

  const duplicateNode = (source) => {
    const clones = [];
    const cloneChildren = (node, parentClientId, position) => {
      const clientId = createPlanningTemplateNodeId();
      clones.push({
        ...node,
        id: undefined,
        client_id: clientId,
        parent_client_id: parentClientId,
        position,
      });

      _.forEach(
        _.sortBy(
          _.filter(draft.nodes, { parent_client_id: node.client_id }),
          'position'
        ),
        (child, childPosition) => cloneChildren(child, clientId, childPosition)
      );
    };

    const nodes = _.map(draft.nodes, (node) =>
      node.parent_client_id === source.parent_client_id &&
      node.position > source.position
        ? { ...node, position: node.position + 1 }
        : node
    );

    cloneChildren(source, source.parent_client_id, source.position + 1);
    updateNodes([...nodes, ...clones]);
  };

  const removeNode = (source) => {
    const removedIds = new Set([source.client_id]);
    let changed = true;

    while (changed) {
      changed = false;
      _.forEach(draft.nodes, (node) => {
        if (
          removedIds.has(node.parent_client_id) &&
          !removedIds.has(node.client_id)
        ) {
          removedIds.add(node.client_id);
          changed = true;
        }
      });
    }

    updateNodes(
      _.filter(draft.nodes, (node) => !removedIds.has(node.client_id))
    );
  };

  const reorderNodes = (collection, oldIndex, newIndex) => {
    if (oldIndex === newIndex) {
      return;
    }

    const parentClientId = collection === 'root' ? null : collection;
    const siblings = _.sortBy(
      _.filter(draft.nodes, { parent_client_id: parentClientId }),
      'position'
    );
    const [moved] = siblings.splice(oldIndex, 1);

    if (!moved) {
      return;
    }

    siblings.splice(newIndex, 0, moved);
    const positions = _.keyBy(
      _.map(siblings, (node, position) => ({
        client_id: node.client_id,
        position,
      })),
      'client_id'
    );

    updateNodes(
      _.map(draft.nodes, (node) =>
        positions[node.client_id]
          ? { ...node, position: positions[node.client_id].position }
          : node
      )
    );
  };

  const templatePayload = () => ({
    name: draft.name_translations,
    description: draft.description_translations || {},
    active: draft.active ? 1 : 0,
    nodes: planningTemplateNodesToPayload(draft.nodes),
  });

  const save = () => {
    if (readOnly) {
      return;
    }

    if (!draft.id) {
      setTableParams('planning_library', {
        template_editor: { isSystem },
      });

      return;
    }

    updatePlanningTemplate(
      draft.id,
      templatePayload(),
      isSystem,
    ).then(({ response }) => {
      setTableParams('planning_library', {
        template_draft: planningTemplateFromResponse(response.data),
        template_dirty: false,
      });
      listPlanningTemplates(isSystem);
      setNotification(
        __('planning-templates.template.saved'),
      );
    });
  };

  const newTemplate = () => {
    if (!canAddTemplate) {
      return;
    }

    setTableParams('planning_library', {
      template_draft: createEmptyPlanningTemplate(languageId),
      template_dirty: false,
    });
  };

  const counts = planningTemplateCounts(draft.nodes);

  return (
    <div className={styles.templateBuilder}>
      <div className={styles.templateToolbar}>
        <button
          type='button'
          className={styles.templateSelector}
          onClick={() =>
            setTableParams('planning_library', {
              template_chooser: {
                canAddTemplate,
                canDeleteSystem,
                isSystem,
              },
            })
          }
        >
          <span className={styles.templateSelectorLabel}>
            {__('planning-templates.builder.template')}
          </span>
          <span className={styles.templateSelectorValue}>
            {draft.name || 'New template'}
          </span>
          <ArrowDownMiddleIcon />
        </button>

        <div className={styles.templateToolbarSpacer} />

        <Button
          medium
          middleText={__('button.save')}
          onClick={save}
          disabled={Boolean(
            readOnly ||
              _.isEmpty(draft.nodes) ||
              (draft.id && !table.template_dirty)
          )}
        />
        <Button
          lightGray
          medium
          middleText={__('planning-templates.builder.new')}
          onClick={newTemplate}
          disabled={!canAddTemplate}
        />
        <Button
          lightGray
          medium
          middleText={__('planning-templates.builder.add-stage')}
          onClick={(event) => openLibrary('stage', {}, event)}
          disabled={readOnly}
        />
      </div>

      <div className={styles.templateSummary}>
        <div>
          <strong>{__('planning-templates.builder.title')}</strong>
          <span>{__('planning-templates.builder.description')}</span>
        </div>
        <div className={styles.templateStats}>
          <span>
            {counts.stages} {__('planning-templates.tabs.stages')}
          </span>
          <span>
            {counts.substages} {__('planning-templates.tabs.substages')}
          </span>
          <span>
            {counts.groups} {__('planning-templates.tabs.groups')}
          </span>
          {table.template_dirty && (
            <span className={styles.unsavedBadge}>
              {__('planning-templates.builder.unsaved')}
            </span>
          )}
        </div>
      </div>

      <div className={styles.templateDepthToolbar}>
        <span>{__('planning-templates.builder.depth')}</span>
        <div className={styles.depthButtons}>
          {[1, 2, 3].map((level) => (
            <button
              type='button'
              key={level}
              className={classNames(level === depth && styles.activeDepth)}
              onClick={() => setDepth(level)}
            >
              {level}
            </button>
          ))}
        </div>
        <span className={styles.depthHelp}>
          {__('planning-templates.builder.drag-help')}
        </span>
      </div>

      <PlanningTemplateTable
        depth={depth}
        nodes={draft.nodes}
        onAdd={openLibrary}
        onDuplicate={duplicateNode}
        onRemove={removeNode}
        onReorder={reorderNodes}
        readOnly={readOnly}
      />

      {libraryPicker && (
        <PlanningTemplateLibraryDropdown
          isSystem={isSystem}
          key={libraryPicker.key}
          languageId={languageId}
          picker={libraryPicker}
          onClose={() => setLibraryPicker(undefined)}
          onSelect={selectLibraryItem}
        />
      )}
    </div>
  );
};

export default PlanningTemplateBuilder;
